import { useQuery, useApolloClient } from "@apollo/client";
import { LIST_STUDIOS, BOOKINGS_BY_STUDIO_ID } from "../../graphql/queries/calendarQueries";
import { useUser } from "../useUser";
import { useState, useEffect, useCallback, useRef } from "react";
import { getCloudFrontImageUrl } from "../../util/cloudFrontUtils";
import { formatBerlinDate, formatBerlinTime } from "../../util/bookingDateTime";
import { filterHostVisibleStudios } from "../../util/studioStatus";
import { buildCoarseLocationText } from "../../util/locationPrivacy";

/**
 * Smart studio data hook with intelligent cache-based refresh
 * This approach minimizes loading states while keeping data fresh
 */
export const useSmartRefreshStudioData = (userID, options = {}) => {
  const {
    pollInterval = 30000, // 30 seconds
    enablePolling = true,
    refreshOnFocus = true,
    refreshOnVisibilityChange = true
  } = options;

  const { user } = useUser(userID);
  const client = useApolloClient();
  const [formattedStudioData, setFormattedStudioData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const pollIntervalRef = useRef(null);
  const lastRefreshTime = useRef(Date.now());
  const isInitialLoad = useRef(true);

  // Query to fetch studios owned by the current user
  const { data: studiosData, loading: studiosLoading, error: studiosError, refetch: refetchStudios } = useQuery(LIST_STUDIOS, {
    variables: {
      filter: {
        userID: { eq: userID }
      }
    },
    skip: !userID,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false // Prevent loading state flicker
  });

  // Function to fetch bookings for a specific studio with smart caching
  const fetchBookingsForStudio = useCallback(async (studioId, useCache = true) => {
    try {
      const { data } = await client.query({
        query: BOOKINGS_BY_STUDIO_ID,
        variables: {
          studioID: studioId
        },
        fetchPolicy: useCache ? 'cache-first' : 'network-only',
        errorPolicy: 'all'
      });
            
      if (!data || !data.bookingsByStudioID || !data.bookingsByStudioID.items) {
        return [];
      }
      
      return data.bookingsByStudioID.items || [];
    } catch (err) {
      console.error("Error fetching bookings for studio:", studioId, err);
      return [];
    }
  }, [client]);

  // Smart background refresh - updates cache without affecting UI loading state
  const smartBackgroundRefresh = useCallback(async () => {
    const now = Date.now();
    const timeSinceLastRefresh = now - lastRefreshTime.current;
    
    // Throttle to prevent excessive calls
    if (timeSinceLastRefresh < 5000) return;
    lastRefreshTime.current = now;

    try {
      // First, silently update studios in background
      const { data: freshStudiosData } = await client.query({
        query: LIST_STUDIOS,
        variables: {
          filter: {
            userID: { eq: userID }
          }
        },
        fetchPolicy: 'network-only',
        errorPolicy: 'all'
      });

      const visibleStudios = filterHostVisibleStudios(freshStudiosData?.listStudios?.items);
      if (!visibleStudios.length) {
        setFormattedStudioData([]);
        return;
      }

      // Then silently update bookings for each studio
      const studioPromises = visibleStudios.map(async (studio) => {
        const freshBookings = await fetchBookingsForStudio(studio.id, false);
        
        // Filter out bookings with excluded statuses
        const excludedStatuses = ['WITHDRAWN', 'CANCELLED', 'EXPIRED', 'NO_SHOW', 'DECLINED', 'REJECTED'];
        const filteredBookings = freshBookings.filter(booking => 
          !excludedStatuses.includes(booking?.status)
        );
        
        // Process the data silently
        const formattedBookings = filteredBookings.map(booking => {
          const startDate = new Date(booking.startDateTime);
          const endDate = new Date(booking.endDateTime);
          
          const processedProfilePicture = booking.User?.profilePicture ? 
            getCloudFrontImageUrl(booking.User.profilePicture, { width: 100, height: 100, fit: 'cover' }) : 
            null;
          
          return {
            date: formatBerlinDate(startDate, { day: '2-digit', month: '2-digit', year: 'numeric' }),
            projectName: booking.projectName,
            type: booking?.projectName ? "Buchung" : "Anfrage",
            status: booking?.status,
            id: booking?.id,
            chatRoomID: booking?.chatRoomID,
            user: booking?.User ? `${booking?.User?.firstName} ${booking?.User?.lastName?.charAt(0)}.` : "Unbekannt",
            startDateTime: booking?.startDateTime,
            endDateTime: booking?.endDateTime,
            startTime: formatBerlinTime(startDate, { hour: '2-digit', minute: '2-digit' }),
            endTime: formatBerlinTime(endDate, { hour: '2-digit', minute: '2-digit' }),
            price: `${booking.totalPrice} Euro`,
            studioLocation: buildCoarseLocationText(studio) || "Unbekannt",
            userData: booking.User ? {
              id: booking.User.id,
              firstName: booking.User.firstName,
              lastName: booking.User.lastName,
              email: booking.User.email,
              phoneNumber: booking.User.phoneNumber,
              profilePicture: booking.User.profilePicture,
              processedProfilePicture: processedProfilePicture
            } : null,
            studioData: {
              minNumberOfHours: studio.minNumberOfHours,
              id: studio.id,
              name: studio.StudioName,
              studioImage: studio.images && studio.images.length > 0 
                ? getCloudFrontImageUrl(studio.images[0], { width: 400, height: 300, fit: 'cover' }) 
                : null
            }
          };
        });

        const studioImage = studio.images && studio.images.length > 0 
          ? getCloudFrontImageUrl(studio.images[0], { width: 400, height: 300, fit: 'cover' }) 
          : null;
        
        return {
          minNumberOfHours: studio.minNumberOfHours,
          studioId: studio.id,
          image: studioImage,
          name: studio.StudioName,
          location: buildCoarseLocationText(studio) || "Unbekannt",
          bookings: formattedBookings
        };
      });

      const processedStudios = await Promise.all(studioPromises);
      
      // Only update state if data actually changed to prevent unnecessary re-renders
      setFormattedStudioData(prevData => {
        const dataChanged = JSON.stringify(prevData) !== JSON.stringify(processedStudios);
        return dataChanged ? processedStudios : prevData;
      });

    } catch (error) {
      console.error("Smart background refresh error:", error);
    }
  }, [client, userID, fetchBookingsForStudio]);

  // Set up intelligent polling
  useEffect(() => {
    if (!enablePolling || !userID) return;

    const startPolling = () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      
      pollIntervalRef.current = setInterval(() => {
        // Only poll if the document is visible and not on initial load
        if (!document.hidden && !isInitialLoad.current) {
          smartBackgroundRefresh();
        }
      }, pollInterval);
    };

    startPolling();

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [userID, pollInterval, enablePolling, smartBackgroundRefresh]);

  // Handle window focus events
  useEffect(() => {
    if (!refreshOnFocus) return;

    const handleFocus = () => {
      if (!isInitialLoad.current) {
        smartBackgroundRefresh();
      }
    };

    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [refreshOnFocus, smartBackgroundRefresh]);

  // Handle page visibility changes
  useEffect(() => {
    if (!refreshOnVisibilityChange) return;

    const handleVisibilityChange = () => {
      if (!document.hidden && !isInitialLoad.current) {
        smartBackgroundRefresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshOnVisibilityChange, smartBackgroundRefresh]);

  // Process initial data load (this is the only time we show loading)
  useEffect(() => {
    const processInitialData = async () => {
      if (studiosLoading) return;
      
      if (studiosError) {
        setError(studiosError);
        setLoading(false);
        isInitialLoad.current = false;
        return;
      }

      try {
        setLoading(true);
        
        const visibleStudios = filterHostVisibleStudios(studiosData?.listStudios?.items);
        if (!visibleStudios.length) {
          setFormattedStudioData([]);
          setLoading(false);
          isInitialLoad.current = false;
          return;
        }
        
        // Process each studio and its bookings for initial load
        const processedStudios = await Promise.all(
          visibleStudios.map(async (studio) => {
            const bookings = await fetchBookingsForStudio(studio.id);
            
            // Filter out bookings with excluded statuses
            const excludedStatuses = ['WITHDRAWN', 'CANCELLED', 'EXPIRED', 'NO_SHOW', 'DECLINED', 'REJECTED'];
            const filteredBookings = bookings.filter(booking => 
              !excludedStatuses.includes(booking?.status)
            );
            
            const formattedBookings = filteredBookings.map(booking => {
              const startDate = new Date(booking.startDateTime);
              const endDate = new Date(booking.endDateTime);
              
              const processedProfilePicture = booking.User?.profilePicture ? 
                getCloudFrontImageUrl(booking.User.profilePicture, { width: 100, height: 100, fit: 'cover' }) : 
                null;
              
              return {
                date: formatBerlinDate(startDate, { day: '2-digit', month: '2-digit', year: 'numeric' }),
                projectName: booking.projectName,
                type: booking?.projectName ? "Buchung" : "Anfrage",
                status: booking?.status,
                id: booking?.id,
                chatRoomID: booking?.chatRoomID,
                user: booking?.User ? `${booking?.User?.firstName} ${booking?.User?.lastName?.charAt(0)}.` : "Unbekannt",
                startDateTime: booking?.startDateTime,
                endDateTime: booking?.endDateTime,
                startTime: formatBerlinTime(startDate, { hour: '2-digit', minute: '2-digit' }),
                endTime: formatBerlinTime(endDate, { hour: '2-digit', minute: '2-digit' }),
                price: `${booking.totalPrice} Euro`,
                studioLocation: buildCoarseLocationText(studio) || "Unbekannt",
                userData: booking.User ? {
                  id: booking.User.id,
                  firstName: booking.User.firstName,
                  lastName: booking.User.lastName,
                  email: booking.User.email,
                  phoneNumber: booking.User.phoneNumber,
                  profilePicture: booking.User.profilePicture,
                  processedProfilePicture: processedProfilePicture
                } : null,
                studioData: {
                  minNumberOfHours: studio.minNumberOfHours,
                  id: studio.id,
                  name: studio.StudioName,
                  studioImage: studio.images && studio.images.length > 0 
                    ? getCloudFrontImageUrl(studio.images[0], { width: 400, height: 300, fit: 'cover' }) 
                    : null
                }
              };
            });

            const studioImage = studio.images && studio.images.length > 0 
              ? getCloudFrontImageUrl(studio.images[0], { width: 400, height: 300, fit: 'cover' }) 
              : null;
            
            return {
              minNumberOfHours: studio.minNumberOfHours,
              studioId: studio.id,
              image: studioImage,
              name: studio.StudioName,
              location: buildCoarseLocationText(studio) || "Unbekannt",
              bookings: formattedBookings
            };
          })
        );

        setFormattedStudioData(processedStudios);
        isInitialLoad.current = false;
      } catch (err) {
        console.error("Error processing initial studio data:", err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    processInitialData();
  }, [studiosData, studiosLoading, studiosError, user, fetchBookingsForStudio]);

  // Manual refresh function for user-triggered actions
  const manualRefresh = useCallback(() => {
    smartBackgroundRefresh();
  }, [smartBackgroundRefresh]);

  return {
    studioData: formattedStudioData,
    loading: isInitialLoad.current ? loading : false, // Only show loading on initial load
    error,
    refetch: manualRefresh,
    isAutoRefreshEnabled: enablePolling
  };
};
